import React, { forwardRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg';
import { InputLabel, InputWrapper, InputError } from 'components/atoms/styleds';

import { ReactComponent as EyeClose } from 'assets/images/eye-close.svg';
import { ReactComponent as EyeOpen } from 'assets/images/eye-open.svg';

import { MultiLangData } from 'common/constants/multi-lang';

import {
  InputStyled,
  SuffixWrapper,
  MultiLangWrapper,
  MultiLangItem,
} from './styled';

const Input = forwardRef(
  (
    {
      value,
      onChange,
      placeholder = '',
      type = 'text',
      label,
      name,
      fullWidth = true,
      style,
      inputStyle,
      autocomplete = 'off',
      error,
      deleteIcon,
      onClickDelete,
      disabled,
      showEyeIcon = true,
      standardWidth,
      multiLangValues = {},
      isMultiLang = false,
      multiline = false,
      ...rest
    },
    ref,
  ) => {
    const [localType, setLocalType] = useState(type);
    const [multiLangKey, setMultiLangKey] = useState('it');
    const showIcon = type === 'password' && showEyeIcon;

    useEffect(() => {
      setLocalType(type);
    }, [type]);

    const onClickShowIcon = useCallback(() => {
      setLocalType((s) => {
        if (s === 'password') {
          return 'text';
        } else {
          return 'password';
        }
      });
    }, [type]);

    return (
      <InputWrapper
        fullWidth={fullWidth}
        height={44}
        standardWidth={standardWidth}
        mb={isMultiLang ? 40 : undefined}
        desktopMb={isMultiLang && 40}
        style={{ display: showIcon ? 'flex' : 'block', ...(style || {}) }}
      >
        <InputStyled
          multiline={multiline}
          ref={ref}
          name={isMultiLang ? `${name}_${multiLangKey}` : name}
          failed={!!error}
          type={localType}
          value={
            isMultiLang ? multiLangValues[multiLangKey] : value
          }
          onChange={onChange}
          placeholder={placeholder}
          style={inputStyle}
          autoComplete={autocomplete}
          disabled={disabled}
          {...rest}
        />
        <InputLabel failed={!!error}>{label}</InputLabel>
        {!!error && <InputError>{error}</InputError>}
        {!!showIcon && (
          <SuffixWrapper
            variant={type}
            role='presentation'
            onClick={onClickShowIcon}
          >
            {localType !== 'password' ? <EyeOpen /> : <EyeClose />}
          </SuffixWrapper>
        )}
        {!!deleteIcon && (
          <SuffixWrapper role='presentation' onClick={onClickDelete}>
            <DeleteIcon />
          </SuffixWrapper>
        )}
        {isMultiLang && (
          <MultiLangWrapper>
            {Object.keys(MultiLangData).map((key) => {
              const item = MultiLangData[key];
              return (
                <MultiLangItem
                  key={key}
                  as={item.icon}
                  isActive={multiLangKey === key}
                  onClick={() => setMultiLangKey(key)}
                />
              );
            })}
          </MultiLangWrapper>
        )}
      </InputWrapper>
    );
  },
);

Input.propTypes = {
  type: PropTypes.oneOf(['text', 'number', 'email', 'password', 'tel']),
  // ref: PropTypes.shape({}),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onClickDelete: PropTypes.func,
  label: PropTypes.string,
  fullWidth: PropTypes.bool,
  style: PropTypes.shape({}),
  inputStyle: PropTypes.shape({}),
  deleteIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  isMultiLang: PropTypes.bool,
};

export default Input;
